'use client';

import { useCallback, useState, useSyncExternalStore } from 'react';
import { useSearchParams } from 'next/navigation';
import {
  ListLimitDialog,
} from '@/new_components/lists/ListLimitDialog';
import { ChevronDown, ListPlus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  APP_NAV_SECTIONS,
  isAppNavActive,
  type AppNavItem,
  type AppNavSection,
} from '@/config/app-nav.config';
import { hasPermission } from '@/lib/permissions';
import { cn } from '@/lib/utils';
import { useLists } from '@/hooks/use-lists';
import { useCreateList } from '@/hooks/use-create-list';
import { useLocale } from '@/hooks/use-locale';
import { useNotifications } from '@/hooks/use-notifications';
import { useSavedSearches } from '@/hooks/use-saved-searches';
import { useSession } from '@/hooks/use-session';
import { useWatchlist } from '@/hooks/use-watchlist';
import { Badge } from '@/components/ui/badge';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { LocaleLink } from '@/components/locale-link';
import { ShellTooltip } from './ShellTooltip';

export const SHELL_FOCUS =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

export function logSidebarClick(id: string) {
  navigator.sendBeacon(
    '/api/log/header-click',
    new Blob([JSON.stringify({ button: `sidebar_${id}` })], {
      type: 'application/json',
    }),
  );
}

interface SidebarNavProps {
  rail?: boolean;
  mobile?: boolean;
  onNavigate?: () => void;
}

const SIDEBAR_PREFERENCE_EVENT = 'firmendata:sidebar-preference';

function useSidebarOpenState(
  key: string,
  defaultOpen: boolean,
  active: boolean,
  mobile = false,
) {
  // Also keeps toggles working when storage is unavailable. Mobile only uses
  // this state and starts fresh when Radix mounts the drawer content again.
  const [fallbackOpen, setFallbackOpen] = useState(defaultOpen);
  const getSnapshot = useCallback(() => {
    if (!mobile) {
      try {
        const stored = window.localStorage.getItem(key);
        if (stored === 'true' || stored === 'false') return stored === 'true';
      } catch {
        // Storage can be blocked by browser privacy settings.
      }
    }
    return fallbackOpen;
  }, [key, mobile, fallbackOpen]);
  const subscribe = useCallback(
    (notify: () => void) => {
      if (mobile) return () => {};
      window.addEventListener('storage', notify);
      window.addEventListener(SIDEBAR_PREFERENCE_EVENT, notify);
      return () => {
        window.removeEventListener('storage', notify);
        window.removeEventListener(SIDEBAR_PREFERENCE_EVENT, notify);
      };
    },
    [mobile],
  );
  const storedOpen = useSyncExternalStore(
    subscribe,
    getSnapshot,
    () => defaultOpen,
  );
  const setOpen = useCallback(
    (nextOpen: boolean) => {
      setFallbackOpen(nextOpen);
      if (!mobile) {
        try {
          window.localStorage.setItem(key, String(nextOpen));
        } catch {
          // Fall back to state for this mounted sidebar.
        }
        // Expanded, rail and desktop overlay instances share the preference.
        window.dispatchEvent(new Event(SIDEBAR_PREFERENCE_EVENT));
      }
    },
    [key, mobile],
  );
  return [mobile ? storedOpen : active || storedOpen, setOpen] as const;
}

export function SidebarNav({
  rail = false,
  mobile = false,
  onNavigate,
}: SidebarNavProps) {
  const { t } = useTranslation();
  const { data: session } = useSession();
  const { internalPathname } = useLocale();
  const tab = useSearchParams().get('tab');
  const { unreadNotificationCount } = useWatchlist();
  const { unreadCount } = useNotifications();
  const { items } = useSavedSearches();
  const counts = {
    'watchlist-unread': unreadNotificationCount,
    'notifications-unread': unreadCount,
    'saved-search-new': items.reduce(
      (sum, item) => sum + Math.max(0, item.new_matches ?? 0),
      0,
    ),
  };
  const sections = APP_NAV_SECTIONS.filter(
    (section) =>
      !section.permission || hasPermission(session, section.permission),
  ).map((section) => ({
    ...section,
    items: section.items.filter(
      (item) => !item.permission || hasPermission(session, item.permission),
    ),
  }));

  return (
    <nav className="space-y-4" aria-label={t('navigation.pages')}>
      {sections.map((section) => (
        <NavSection
          key={mobile ? `${section.id}-${internalPathname}-${tab}` : section.id}
          section={section}
          rail={rail}
          mobile={mobile}
          active={(item) => isAppNavActive(item, internalPathname, tab)}
          counts={counts}
          onNavigate={onNavigate}
        />
      ))}
    </nav>
  );
}

function NavSection({
  section,
  rail,
  mobile,
  active,
  counts,
  onNavigate,
}: SidebarNavProps & {
  section: AppNavSection;
  active: (item: AppNavItem) => boolean;
  counts: Record<NonNullable<AppNavItem['badge']>, number>;
}) {
  const { t } = useTranslation();
  const { internalPathname } = useLocale();
  const hasActive = section.items.some(active);
  const [open, setOpen] = useSidebarOpenState(
    `fd_sidebar_section_${section.id}`,
    mobile
      ? hasActive
      : section.id === 'admin'
        ? internalPathname === '/admin' ||
          internalPathname.startsWith('/admin/')
        : true,
    hasActive,
    mobile,
  );
  const rows = (
    <div className="space-y-1">
      {section.items.map((item) => (
        <NavItem
          key={item.id}
          item={item}
          active={active(item)}
          count={item.badge ? counts[item.badge] : 0}
          rail={rail}
          mobile={mobile}
          onNavigate={onNavigate}
        />
      ))}
    </div>
  );
  const headingClass = cn(
    'fd-mono flex w-full items-center justify-between px-3 text-[11px] uppercase tracking-[0.08em] text-[var(--fd-muted)]',
    mobile ? 'h-11' : 'min-h-9',
  );
  if (!section.titleKey) return rows;
  if (rail)
    return (
      <div>
        <div className="mx-3 mb-3 border-t border-[var(--fd-line)]" />
        {rows}
      </div>
    );
  if (section.collapsible || mobile)
    return (
      <Collapsible open={open} onOpenChange={setOpen}>
        <CollapsibleTrigger
          className={cn(headingClass, 'rounded-md', SHELL_FOCUS)}
        >
          {t(section.titleKey)}
          <ChevronDown
            aria-hidden
            className={cn(
              'size-3.5 transition-transform',
              open && 'rotate-180',
            )}
          />
        </CollapsibleTrigger>
        <CollapsibleContent>{rows}</CollapsibleContent>
      </Collapsible>
    );
  return (
    <div>
      <div className={headingClass}>{t(section.titleKey)}</div>
      {rows}
    </div>
  );
}

function NavItem({
  item,
  active,
  count,
  rail,
  mobile,
  onNavigate,
}: SidebarNavProps & { item: AppNavItem; active: boolean; count: number }) {
  const { t } = useTranslation();
  const [listsOpen, setListsOpen] = useSidebarOpenState(
    'fd_sidebar_lists_open',
    active,
    active,
    mobile || item.id !== 'my_lists',
  );
  const Icon = item.icon;
  const rowClass = cn(
    'relative flex min-w-0 items-center gap-1.5 rounded-md text-[var(--fd-muted)] hover:bg-[var(--fd-bg)] hover:text-[var(--fd-ink)]',
    SHELL_FOCUS,
    rail
      ? 'mx-auto size-10 justify-center'
      : mobile
        ? 'h-11 px-3 text-[15px]'
        : 'h-9 px-3 text-[14px]',
    active &&
      'bg-[var(--fd-card)] text-[var(--fd-ink)] before:absolute before:inset-y-2 before:left-0 before:w-0.5 before:rounded-full before:bg-[var(--fd-accent-ink)]',
  );
  const label = t(item.titleKey);
  const link = (
    <LocaleLink
      href={item.path}
      hardNavigate={item.hardNavigate}
      aria-label={rail ? label : undefined}
      aria-current={active ? 'page' : undefined}
      className={cn(rowClass, item.id === 'my_lists' && !rail && 'grow')}
      onClick={() => {
        logSidebarClick(item.id);
        onNavigate?.();
      }}
    >
      <span className="relative shrink-0">
        <Icon aria-hidden className={mobile ? 'size-5' : 'size-[18px]'} />
        {rail && count > 0 && (
          <span
            aria-hidden
            className="absolute -right-1 -top-1 size-1.5 rounded-full bg-[var(--fd-accent-ink)]"
          />
        )}
      </span>
      {!rail && (
        <>
          <span className="min-w-0 flex-1 truncate">{label}</span>
          {count > 0 && (
            <Badge
              size="sm"
              shape="circle"
              className="h-5 min-w-5 shrink-0 bg-[var(--fd-bg)] px-1.5 text-[11px] tabular-nums text-[var(--fd-accent-ink)]"
            >
              {item.badge === 'saved-search-new'
                ? `+${count}`
                : count > 99
                  ? '99+'
                  : count}
            </Badge>
          )}
        </>
      )}
    </LocaleLink>
  );
  if (rail) return <ShellTooltip label={label}>{link}</ShellTooltip>;
  if (item.id === 'my_lists')
    return (
      <Collapsible open={listsOpen} onOpenChange={setListsOpen}>
        <div className="flex items-center">
          {link}
          <CollapsibleTrigger
            aria-label={t(
              listsOpen ? 'navigation.collapseLists' : 'navigation.expandLists',
            )}
            className={cn(
              'flex size-10 shrink-0 items-center justify-center rounded-md text-[var(--fd-muted)] hover:bg-[var(--fd-bg)]',
              SHELL_FOCUS,
            )}
          >
            <ChevronDown
              aria-hidden
              className={cn(
                'size-4 transition-transform',
                listsOpen && 'rotate-180',
              )}
            />
          </CollapsibleTrigger>
        </div>
        <CollapsibleContent>
          <RecentLists mobile={mobile} onNavigate={onNavigate} />
        </CollapsibleContent>
      </Collapsible>
    );
  return link;
}

function RecentLists({ mobile, onNavigate }: SidebarNavProps) {
  const { t } = useTranslation();
  const { internalPathname } = useLocale();
  const { lists } = useLists();
  const { createList: handleNewList, isCreating: isCreatingList, gate: listGate, closeGate } = useCreateList(onNavigate);
  const rowClass = cn(
    'flex w-full min-w-0 items-center gap-2 rounded-md px-3 text-[var(--fd-muted)] hover:bg-[var(--fd-bg)] hover:text-[var(--fd-ink)]',
    mobile ? 'min-h-11 text-[15px]' : 'min-h-9 text-[14px]',
    SHELL_FOCUS,
  );
  const recent = [...lists]
    .sort((a, b) => Date.parse(b.updated_at) - Date.parse(a.updated_at))
    .slice(0, 5);
  return (
    <div className="ml-6 space-y-1 border-l border-[var(--fd-line)] pl-1">
      {recent.map((list) => (
        <LocaleLink
          key={list.id}
          href={`/my-lists/${list.id}`}
          className={rowClass}
          aria-current={
            internalPathname === `/my-lists/${list.id}` ? 'page' : undefined
          }
          onClick={() => {
            logSidebarClick('list');
            onNavigate?.();
          }}
        >
          <span className="min-w-0 grow truncate">{list.name}</span>
          <span
            className="shrink-0 text-xs"
            aria-label={t('lists.companyCount', { count: list.company_count })}
          >
            {list.company_count}
          </span>
        </LocaleLink>
      ))}
      <button
        type="button"
        className={rowClass}
        disabled={isCreatingList}
        onClick={() => {
          logSidebarClick('new_list');
          void handleNewList();
        }}
      >
        <ListPlus className="size-4 shrink-0" aria-hidden />
        {t('lists.newList')}
      </button>
      <LocaleLink
        href="/my-lists"
        className={rowClass}
        onClick={() => {
          logSidebarClick('all_lists');
          onNavigate?.();
        }}
      >
        {t('navigation.allLists')}
      </LocaleLink>
      <ListLimitDialog
        open={listGate != null}
        onOpenChange={(open) => !open && closeGate()}
        variant={listGate?.variant ?? 'pro_required'}
        limit={listGate?.limit}
      />
    </div>
  );
}
